import { ColumnDef, ExpandedState } from '@tanstack/react-table';
import { Table, Badge, Button, Icon, Spinner } from 'ui';
import { IGetBomResponse, IGetDrawingResponse } from 'modules/billOfMaterials/types';
import { useLazyGetBomQuery, useLazyGetDrawingQuery } from 'modules/billOfMaterials/bomApi';
import {
  useLazyGetReferenceQuery,
  useLazyOcrSearchReferencesQuery,
} from 'modules/threads/threadsApi';
import colors from 'theme/colors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from 'components/ui/input';
import {
  IFileVersions,
  IOcrSearchMatchMode,
  IOcrSearchResult,
  IReferences,
} from 'modules/threads/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { handleDownloadFile } from 'utils/helpers';
import { showErrorMessage, showSuccessMessage } from 'modules/alert/utils';
import { NOTIFICATION } from 'modules/alert/constants';
import { useSearchParams, useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import { getFileType } from 'utils/getFileType';
import { useModal } from 'modules/modals/ModalProvider';
import {
  IBomViewerContext,
  IBomViewerFileNode,
  IBomViewerSearchState,
  ICurrentBomState,
  IOcrSearchTarget,
} from 'pages/documentViewer/types';
import { fetchBomGroup } from '../helpers';

interface Part {
  itemNumber: string | number;
  partNumber: string | number;
  quantity: string | number;
  unit: string;
  description: string;
  drawing: string;
  reference: IReferences | undefined | null;
  isSubAssembly?: boolean;
  latestFile: IFileVersions | null;
  children?: Part[];
}

const OCR_PAGE_SIZE = 50;
const DEFAULT_OCR_MATCH_MODE: IOcrSearchMatchMode = 'partial';

const partColumns = (
  close: () => void,
  currentBomState: ICurrentBomState,
  navigate: ReturnType<typeof useNavigate>,
  bomViewerContext: IBomViewerContext
): ColumnDef<Part>[] => [
  {
    accessorKey: 'itemNumber',
    header: 'Item Number',
  },
  {
    accessorKey: 'partNumber',
    header: 'Part Number',
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
  },
  {
    accessorKey: 'unit',
    header: 'Units',
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row, getValue }) => {
      const value = getValue<string>();
      return <p className="line-clamp-2 flex-1 overflow-hidden text-ellipsis">{value}</p>;
    },
  },
  {
    accessorKey: 'drawing',
    header: 'Drawing',
    size: 400,
    cell: ({ row, getValue }) => {
      const [searchParams] = useSearchParams();
      const threadState = searchParams.toString() || '';

      const value = getValue<string>();
      const isSubAssembly = row.original.isSubAssembly;
      const latestFile = row.original.latestFile;
      const reference = row.original.reference;

      const type = getFileType(reference?.fileName ?? undefined);
      const unsupported = type === 'unknown' || type === 'other';

      const handleOpenDocument = () => {
        close();
        // Defer navigation to the next tick so the modal can close cleanly before the route changes.
        setTimeout(() => {
          navigate(`${routes.documentViewer}/${reference?.id}?${threadState}`, {
            state: {
              currentBomState,
              bomViewerContext,
            },
          });
        }, 0);
      };

      return (
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="line-clamp-1 flex-1 overflow-hidden text-ellipsis">{value}</p>
            {isSubAssembly && (
              <Badge backgroundColor={colors.blue} id="sub-assembly-badge">
                <p className="font-semibold text-dark1">Sub-Assembly</p>
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            {latestFile && (
              <button
                className="flex h-8 w-[40px] items-center justify-center rounded bg-background p-2
                  font-normal text-white [&_path]:stroke-current"
                type="button"
                onClick={handleDownloadFile(value, latestFile.file)}
              >
                <Icon size="normal" icon="DownloadIcon" />
              </button>
            )}
            {reference && !unsupported && (
              <button
                type="button"
                onClick={handleOpenDocument}
                className="flex h-8 w-[40px] items-center justify-center rounded bg-background
                  p-2 font-normal text-white"
              >
                <Icon size="medium" stroke="transparent" color="#ffffff" icon="EyeIcon" />
              </button>
            )}
          </div>
        </div>
      );
    },
  },
];

interface IViewBom {
  bom: IGetBomResponse;
  threadId: number;
  expandedState?: ExpandedState;
  onEditBom?: () => Promise<void> | void;
}

const buildPartData = (
  bom: IGetBomResponse,
  subBoms: Record<number, IGetBomResponse>,
  drawings: Record<number, IGetDrawingResponse>,
  references: Record<number, IReferences>
): Part[] => {
  return bom.items.map(
    ({
      item_number,
      part_number,
      quantity,
      unit,
      description,
      drawing_info,
      item_type,
      sub_assembly_bom,
      drawing,
    }): Part => {
      const isSubAssembly = item_type === 'SUB_ASSEMBLY';
      const children =
        isSubAssembly && sub_assembly_bom && subBoms[sub_assembly_bom]
          ? buildPartData(subBoms[sub_assembly_bom], subBoms, drawings, references)
          : [];

      // Find the drawing in the drawings that matches this items id
      const matchedDrawing = drawing !== undefined ? drawings[drawing] : null;

      // Find the corresponding reference from referenceList
      const matchedReference =
        matchedDrawing?.reference !== undefined
          ? (references[matchedDrawing.reference] ?? null)
          : null;

      const latestFile =
        matchedReference && matchedReference.fileVersions.length
          ? matchedReference.fileVersions[0]
          : null;

      return {
        itemNumber: item_number,
        partNumber: part_number,
        quantity: parseFloat(quantity) ?? 1,
        description: description ?? '-',
        drawing: drawing_info?.title ?? 'N/A',
        reference: matchedReference,
        unit,
        isSubAssembly,
        latestFile,
        children,
      };
    }
  );
};

const buildBomViewerFileTree = (parts: Part[], parentPath: string[] = []): IBomViewerFileNode[] => {
  return parts.map((part, index) => {
    const nodePath = [...parentPath, `${part.itemNumber}-${index}`];
    const children = buildBomViewerFileTree(part.children ?? [], nodePath);
    const referenceId =
      part.reference?.id !== undefined && part.reference?.id !== null
        ? Number(part.reference.id)
        : null;

    const hasReference = Number.isFinite(referenceId);
    const fileType = getFileType(part.reference?.fileName ?? undefined);
    const isPreviewable =
      hasReference && fileType !== 'unknown' && fileType !== 'other' && fileType !== 'link';
    const nodeLabel = `${part.itemNumber}: ${part.partNumber}`;

    return {
      id: nodePath.join('.'),
      label: nodeLabel,
      referenceId: hasReference ? (referenceId as number) : null,
      referenceName: part.reference?.name ?? null,
      isPreviewable,
      children,
    };
  });
};

const getOcrSnippetForClipboard = (result: IOcrSearchResult): string =>
  result.snippet || result.text || '';

const ViewBom: React.FC<IViewBom> = ({ bom, threadId, expandedState, onEditBom }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { close: closeModal } = useModal();

  const [triggerGetBom] = useLazyGetBomQuery();
  const [triggerGetDrawing] = useLazyGetDrawingQuery();
  const [triggerGetReference] = useLazyGetReferenceQuery();

  const [expanded, setExpanded] = useState<ExpandedState>(expandedState ?? {});
  const [ocrQuery, setOcrQuery] = useState('');
  const [submittedOcrQuery, setSubmittedOcrQuery] = useState('');
  const [ocrMatchMode, setOcrMatchMode] = useState<IOcrSearchMatchMode>(DEFAULT_OCR_MATCH_MODE);
  const [ocrResults, setOcrResults] = useState<IOcrSearchResult[]>([]);
  const [ocrTotalCount, setOcrTotalCount] = useState<number | null>(null);
  const [ocrNextUrl, setOcrNextUrl] = useState<string | null>(null);
  const [isOcrSearching, setIsOcrSearching] = useState(false);
  const [isOcrSearchError, setIsOcrSearchError] = useState(false);
  const [loadingGroupData, setLoadingGroupData] = useState(true);
  const [groupData, setGroupData] = useState<{
    subBoms: Record<number, IGetBomResponse>;
    drawings: Record<number, IGetDrawingResponse>;
    references: Record<number, IReferences>;
  }>({
    subBoms: {},
    drawings: {},
    references: {},
  });

  const [triggerOcrSearch] = useLazyOcrSearchReferencesQuery();

  const runOcrSearch = useCallback(
    async (args: {
      query: string;
      append: boolean;
      nextUrl?: string | null;
      matchMode?: IOcrSearchMatchMode;
    }) => {
      const trimmed = args.query.trim();
      if (!trimmed) return;

      setIsOcrSearching(true);
      setIsOcrSearchError(false);

      try {
        const matchMode = args.matchMode ?? DEFAULT_OCR_MATCH_MODE;
        const page = await triggerOcrSearch(
          args.nextUrl
            ? { q: trimmed, threadId, nextUrl: args.nextUrl }
            : {
                q: trimmed,
                threadId,
                match_mode: matchMode,
                limit: OCR_PAGE_SIZE,
                offset: 0,
              }
        ).unwrap();

        setOcrTotalCount(page.count);
        setOcrNextUrl(page.next);

        setOcrResults((prev) => (args.append ? [...prev, ...page.results] : page.results));
      } catch {
        setIsOcrSearchError(true);
      } finally {
        setIsOcrSearching(false);
      }
    },
    [threadId, triggerOcrSearch]
  );

  useEffect(() => {
    if (!submittedOcrQuery.trim()) {
      setOcrResults([]);
      setOcrTotalCount(null);
      setOcrNextUrl(null);
      setIsOcrSearchError(false);
      return;
    }

    runOcrSearch({ query: submittedOcrQuery, append: false, matchMode: ocrMatchMode }).catch(
      () => {}
    );
  }, [ocrMatchMode, runOcrSearch, submittedOcrQuery]);

  useEffect(() => {
    fetchBomGroup(bom.id, triggerGetBom, triggerGetDrawing, triggerGetReference)
      .then(({ subBoms, drawings, references }) => {
        setGroupData({ subBoms, drawings, references });
      })
      .catch(() => showErrorMessage(NOTIFICATION.BOM_GET_FAILURE))
      .finally(() => setLoadingGroupData(false));
  }, [bom.id]);

  const partData = buildPartData(bom, groupData.subBoms, groupData.drawings, groupData.references);
  const bomViewerContext: IBomViewerContext = {
    threadId,
    fileTree: buildBomViewerFileTree(partData),
    search: {
      query: ocrQuery,
      submittedQuery: submittedOcrQuery,
      matchMode: ocrMatchMode,
      results: ocrResults,
      totalCount: ocrTotalCount,
      nextUrl: ocrNextUrl,
    },
  };
  const bomViewerSearchStateForNavigation: IBomViewerSearchState = {
    query: ocrQuery,
    submittedQuery: submittedOcrQuery,
    matchMode: ocrMatchMode,
    results: [],
    totalCount: null,
    nextUrl: null,
  };
  const bomViewerContextForNavigation: IBomViewerContext = {
    ...bomViewerContext,
    search: bomViewerSearchStateForNavigation,
  };
  const bomReferenceIds = useMemo(() => {
    const ids = new Set<number>();
    const stack = [...bomViewerContext.fileTree];

    while (stack.length > 0) {
      const current = stack.pop();
      if (current) {
        if (current.referenceId !== null) {
          ids.add(current.referenceId);
        }

        stack.push(...current.children);
      }
    }

    return ids;
  }, [bomViewerContext.fileTree]);

  const { inBomResults, outsideBomResults } = useMemo(() => {
    const inBom: IOcrSearchResult[] = [];
    const outsideBom: IOcrSearchResult[] = [];

    ocrResults.forEach((result) => {
      if (bomReferenceIds.has(result.referenceId)) {
        inBom.push(result);
      } else {
        outsideBom.push(result);
      }
    });

    return { inBomResults: inBom, outsideBomResults: outsideBom };
  }, [bomReferenceIds, ocrResults]);
  const tooltipText =
    'Sub-Assembly can not be expanded because the maximum nesting depth has been reached.';
  const noItemsText = 'This Bill of Material is empty, as it has no parts associated with it.';

  const currentBomState = { bom, threadId, expanded };
  const threadState = searchParams.toString() || '';

  if (loadingGroupData)
    return (
      <div className="flex items-center justify-center">
        <Spinner size="medium" />
      </div>
    );

  const handleOcrSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmittedOcrQuery(ocrQuery.trim());
  };

  const clearOcrSearch = () => {
    setOcrQuery('');
    setSubmittedOcrQuery('');
    setOcrResults([]);
    setOcrTotalCount(null);
    setOcrNextUrl(null);
    setIsOcrSearchError(false);
  };

  const handleMatchModeChange = (value: string) => {
    const nextMode = value === 'strict' ? 'strict' : DEFAULT_OCR_MATCH_MODE;
    if (nextMode === ocrMatchMode) return;
    setOcrMatchMode(nextMode);
    setOcrResults([]);
    setOcrTotalCount(null);
    setOcrNextUrl(null);
  };

  const handleLoadMoreOcr = () => {
    if (!ocrNextUrl || !submittedOcrQuery.trim()) return;
    runOcrSearch({
      query: submittedOcrQuery,
      append: true,
      nextUrl: ocrNextUrl,
      matchMode: ocrMatchMode,
    }).catch(() => {});
  };

  const handleOpenOcrResult = (result: IOcrSearchResult) => {
    const ocrSearchTarget: IOcrSearchTarget = {
      pageNumber: result.pageNumber,
      pageWidth: result.pageWidth,
      pageHeight: result.pageHeight,
      pageUnit: result.pageUnit,
      pageAngle: result.pageAngle,
      boundingBox: result.boundingBox,
      polygon: result.polygon,
      snippet: result.snippet,
      referenceVersionedFileId: result.referenceVersionedFileId,
    };
    closeModal();
    setTimeout(() => {
      navigate(`${routes.documentViewer}/${result.referenceId}?${threadState}`, {
        state: {
          currentBomState,
          bomViewerContext: bomViewerContextForNavigation,
          ocrSearchTarget,
        },
      });
    }, 0);
  };

  const handleCopyOcrResult = async (
    event: React.MouseEvent<HTMLButtonElement>,
    result: IOcrSearchResult
  ) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const snippet = getOcrSnippetForClipboard(result);
      if (!snippet.trim()) {
        showErrorMessage(NOTIFICATION.CLIPBOARD_COPY_FAILURE);
        return;
      }

      await navigator.clipboard.writeText(snippet);
      showSuccessMessage('Snippet copied');
    } catch {
      showErrorMessage(NOTIFICATION.CLIPBOARD_COPY_FAILURE);
    }
  };

  const handleEditBomFromView = async () => {
    closeModal();
    if (onEditBom) {
      await Promise.resolve(onEditBom());
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      {onEditBom && (
        <div className="flex justify-end">
          <Button
            type="button"
            color={colors.orange}
            variant="outlined"
            onClick={handleEditBomFromView}
          >
            Edit BOM
          </Button>
        </div>
      )}
      <div className="border-dark3 rounded-md border bg-dark2 p-4">
        <form className="flex flex-wrap items-end gap-2" onSubmit={handleOcrSearch}>
          <div className="min-w-[280px] flex-1">
            <p className="mb-2 text-sm font-medium">OCR search</p>
            <Input
              id="bom-ocr-search-input"
              aria-label="OCR search"
              value={ocrQuery}
              onChange={(event) => setOcrQuery(event.target.value)}
              placeholder="Search drawings, notes, part numbers, BOM text"
            />
          </div>
          <div className="w-[200px]">
            <p className="mb-2 text-sm font-medium">Search Mode</p>
            <Select value={ocrMatchMode} onValueChange={handleMatchModeChange}>
              <SelectTrigger aria-label="Search Mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="partial">Partial match</SelectItem>
                <SelectItem value="strict">Strict match</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" color={colors.orange} variant="filled" disabled={!ocrQuery.trim()}>
            Search
          </Button>
          {submittedOcrQuery && (
            <Button type="button" variant="outlined" color={colors.gray3} onClick={clearOcrSearch}>
              Clear
            </Button>
          )}
        </form>
        {submittedOcrQuery && (
          <div className="mt-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">OCR results</p>
                <p className="text-sm" style={{ color: colors.gray3 }}>
                  Showing line-level matches for{' '}
                  <span className="font-medium">{submittedOcrQuery}</span>
                </p>
              </div>
              {!isOcrSearching && !isOcrSearchError && (
                <p className="text-sm" style={{ color: colors.gray3 }}>
                  {ocrTotalCount != null
                    ? `${ocrResults.length} of ${ocrTotalCount} result(s)`
                    : `${ocrResults.length} result(s)`}
                </p>
              )}
            </div>
            {isOcrSearching && (
              <p className="text-sm" style={{ color: colors.gray3 }}>
                Searching drawings...
              </p>
            )}
            {isOcrSearchError && (
              <p className="text-sm text-app-red">Unable to load OCR search results right now.</p>
            )}
            {!isOcrSearching && !isOcrSearchError && ocrResults.length === 0 && (
              <p className="text-sm" style={{ color: colors.gray3 }}>
                No OCR matches found in this BOM context.
              </p>
            )}
            {!isOcrSearching && !isOcrSearchError && ocrResults.length > 0 && (
              <>
                <div className="flex max-h-[280px] flex-col gap-2 overflow-y-auto">
                  {inBomResults.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-orange-300">
                        In current BOM ({inBomResults.length})
                      </p>
                      {inBomResults.map((result, index) => (
                        <div
                          key={[
                            result.referenceVersionedFileId,
                            result.pageNumber,
                            result.orderIndex,
                            result.contentOffsetStart,
                            index,
                          ].join('-')}
                          className="border-dark3 rounded-md border border-l-4 border-l-orange-500
                            bg-dark1 px-3 py-2 text-left transition-colors hover:border-orange-500"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <button
                              type="button"
                              onClick={() => handleOpenOcrResult(result)}
                              className="min-w-0 flex-1 text-left"
                            >
                              <p className="truncate font-medium">{result.referenceName}</p>
                              <p className="line-clamp-2 text-sm" style={{ color: colors.gray3 }}>
                                {result.snippet || result.text}
                              </p>
                            </button>
                            <div className="flex shrink-0 items-start gap-2">
                              <span
                                className="text-xs uppercase tracking-wide"
                                style={{ color: colors.gray3 }}
                              >
                                Page {result.pageNumber}
                              </span>
                              <button
                                type="button"
                                onClick={(event) => handleCopyOcrResult(event, result)}
                                className="rounded p-1 text-gray-300 transition-colors hover:text-white"
                                aria-label={`Copy snippet from ${result.referenceName}`}
                              >
                                <Icon icon="CopyLinkIcon" size="small" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {outsideBomResults.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                        Outside current BOM ({outsideBomResults.length})
                      </p>
                      {outsideBomResults.map((result, index) => (
                        <div
                          key={[
                            'outside',
                            result.referenceVersionedFileId,
                            result.pageNumber,
                            result.orderIndex,
                            result.contentOffsetStart,
                            index,
                          ].join('-')}
                          className="border-dark3 rounded-md border border-l-4 border-l-gray-500
                            bg-dark1 px-3 py-2 text-left transition-colors hover:border-orange-500"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <button
                              type="button"
                              onClick={() => handleOpenOcrResult(result)}
                              className="min-w-0 flex-1 text-left"
                            >
                              <p className="truncate font-medium">{result.referenceName}</p>
                              <p className="line-clamp-2 text-sm" style={{ color: colors.gray3 }}>
                                {result.snippet || result.text}
                              </p>
                            </button>
                            <div className="flex shrink-0 items-start gap-2">
                              <span
                                className="text-xs uppercase tracking-wide"
                                style={{ color: colors.gray3 }}
                              >
                                Page {result.pageNumber}
                              </span>
                              <button
                                type="button"
                                onClick={(event) => handleCopyOcrResult(event, result)}
                                className="rounded p-1 text-gray-300 transition-colors hover:text-white"
                                aria-label={`Copy snippet from ${result.referenceName}`}
                              >
                                <Icon icon="CopyLinkIcon" size="small" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {ocrNextUrl && (
                  <div className="mt-3 flex justify-center">
                    <Button
                      type="button"
                      color={colors.orange}
                      variant="outlined"
                      disabled={isOcrSearching}
                      onClick={handleLoadMoreOcr}
                    >
                      Load more
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <Table<Part>
        columns={partColumns(closeModal, currentBomState, navigate, bomViewerContextForNavigation)}
        data={partData}
        size={1000}
        noItemsText={noItemsText}
        nestingDepthTooltipText={tooltipText}
        expanded={expanded}
        setExpanded={setExpanded}
      />
    </div>
  );
};

export default ViewBom;
